// Local event logger (IndexedDB with localStorage fallback)
// - Automatically registers to storeEvents.use to capture both event and track payloads
// - Persists with minimal overhead; flushes on visibility change
// - Exposes window.storeLogs API: add, getAll, clear, exportJSON

const DB_NAME = 'fr-logs';
const STORE = 'events';
const LS_KEY = 'fr:logs';
const BUFFER_LIMIT = 100; // in-memory before writing
const LS_LIMIT = 500;     // ring size for localStorage fallback

function uid() {
  try { return crypto.randomUUID(); } catch (_) { return String(Date.now()) + '-' + Math.random().toString(16).slice(2); }
}

function getIds() {
  try {
    const ls = window.localStorage;
    let anonId = ls.getItem('fr:anonId');
    if (!anonId) { anonId = uid(); ls.setItem('fr:anonId', anonId); }
    let sessionId = ls.getItem('fr:sessionId');
    const last = parseInt(ls.getItem('fr:session:ts') || '0', 10) || 0;
    const now = Date.now();
    if (!sessionId || now - last > 30 * 60 * 1000) { // 30 min idle -> new session
      sessionId = uid();
    }
    ls.setItem('fr:sessionId', sessionId);
    ls.setItem('fr:session:ts', String(now));
    return { anonId, sessionId };
  } catch (_) { return { anonId: 'anon', sessionId: 'sess' }; }
}

function openDB() {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        try { const db = req.result; if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' }); } catch (_) {}
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) { reject(e); }
  });
}

async function dbAdd(db, rec) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
      const st = tx.objectStore(STORE);
      st.put(rec);
    } catch (e) { reject(e); }
  });
}

async function dbGetAll(db) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const st = tx.objectStore(STORE);
      const req = st.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    } catch (e) { reject(e); }
  });
}

async function dbClear(db) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      const st = tx.objectStore(STORE);
      const req = st.clear();
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error);
    } catch (e) { reject(e); }
  });
}

function lsRead() {
  try { const raw = localStorage.getItem(LS_KEY); return raw ? JSON.parse(raw) : []; } catch (_) { return []; }
}
function lsWrite(items) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(items.slice(-LS_LIMIT))); } catch (_) {}
}

const buffer = [];
let db = null;
let dbReady = false;
let enabled = true;

(async () => { try { db = await openDB(); dbReady = true; } catch (_) { db = null; dbReady = false; } })();

function persist(rec) {
  if (!rec) return;
  if (!enabled) return;
  buffer.push(rec);
  if (buffer.length >= BUFFER_LIMIT) flush();
}

async function flush() {
  try {
    const items = buffer.splice(0, buffer.length);
    if (!items.length) return;
    if (dbReady && db) {
      for (const it of items) { try { await dbAdd(db, it); } catch (_) { /* fallback below */ lsFallback(items); break; } }
    } else {
      lsFallback(items);
    }
  } catch (_) {}
}

function lsFallback(items) {
  try { const prev = lsRead(); lsWrite(prev.concat(items)); } catch (_) {}
}

function buildRecord(payload) {
  const ids = getIds();
  return {
    id: uid(),
    ts: Date.now(),
    url: (location && location.href) || '',
    anonId: ids.anonId,
    sessionId: ids.sessionId,
    ...payload
  };
}

// Plugin registration
try {
  if (window.storeEvents && window.storeEvents.use) {
    window.storeEvents.use((p) => {
      try {
        if (!p) return;
        if (p.type === 'event') persist(buildRecord({ kind: 'event', name: p.name, detail: p.detail }));
        else if (p.type === 'track') persist(buildRecord({ kind: 'track', name: p.name, props: p.props }));
      } catch (_) {}
    });
  }
} catch (_) {}

// Flush on page hide
try { document.addEventListener('visibilitychange', () => { try { if (document.visibilityState !== 'visible') flush(); } catch (_) {} }, true); } catch (_) {}
try { window.addEventListener('pagehide', () => { try { flush(); } catch (_) {} }, true); } catch (_) {}
// Periodic flush during activity (every 5s best-effort)
try { setInterval(() => { try { flush(); } catch (_) {} }, 5000); } catch (_) {}

// Public API
try {
  const api = {
    enable: (v) => { try { enabled = !!v; } catch (_) {} },
    add: (payload) => { try { persist(buildRecord({ kind: 'custom', payload })); } catch (_) {} },
    getAll: async () => {
      try {
        let persisted = [];
        if (dbReady && db) persisted = await dbGetAll(db); else persisted = lsRead();
        // Include in-memory buffer snapshot
        const pending = buffer.slice();
        return persisted.concat(pending);
      } catch (_) { return lsRead().concat(buffer.slice()); }
    },
    clear: async () => { try { if (dbReady && db) await dbClear(db); lsWrite([]); } catch (_) { lsWrite([]); } },
    exportJSON: async () => { try { const all = await api.getAll(); return JSON.stringify(all); } catch (_) { return '[]'; } }
  };
  window.storeLogs = api;
} catch (_) {}


